import classNames from 'classnames';
import React from 'react';

import ScoreTab from 'components/common/scoreTab';

import type { PermissionMap } from 'pages/settings/users/userPermissions/types';
import type { LegacySkill, LegacySkillCategory, SkillFormValues } from '../types';

type SkillNodeProps = {
  skills: LegacySkill[];
  selectedSkills?: LegacySkill[];
  rating?: boolean;
  activeSkill?: LegacySkill | SkillFormValues | null;
  isHr?: boolean;
  permissions?: PermissionMap;
  category?: string;
  handleAddClick?: () => void;
  onSkillAdd?: (skillCategory: LegacySkillCategory) => void;
  raterState?: number | string | null;
  setRaterState?: React.Dispatch<React.SetStateAction<number | string | null>>;
  skill: LegacySkill;
  onRating?: (skill: LegacySkill, rating: number | null) => void;
  onSkillUncheck?: (skill: LegacySkill) => void;
  onClick?: (skill: LegacySkill) => void;
};

const SkillNode: React.FC<SkillNodeProps> = ({
  skills,
  selectedSkills,
  rating,
  activeSkill,
  raterState,
  setRaterState,
  skill,
  onRating,
  onSkillUncheck,
  onClick,
}) => {
  const selected = selectedSkills?.find((selectedSkill) => selectedSkill.id === skill.id);
  const isActive = rating ? Boolean(selected) : activeSkill?.id === skill.id;

  const getSkillByName = (name: string) => skills.find((selectedSkill) => selectedSkill.name === name);

  const handleRating = (skillName: string, ratingValue: number | null) => {
    const effectedSkill = getSkillByName(skillName);

    if (effectedSkill && onRating) {
      onRating(effectedSkill, ratingValue ? Number(ratingValue) : null);
    }

    setRaterState?.(null);
  };

  const isRaterOpen = raterState === skill.id;

  return (
    <div className="skill-node">
      {isRaterOpen && (
        <div className="skill-node-rating">
          <ScoreTab
            id={String(skill.id ?? skill.name)}
            name={skill.name}
            scoreValue={selected?.rating ?? null}
            handleChange={handleRating}
          />
        </div>
      )}
      <span
        className={classNames('skill', {
          'active-skill': isActive,
        })}
        onClick={() => {
          if (rating) {
            if (isRaterOpen) {
              setRaterState?.(null);
            } else if (isActive && onSkillUncheck) {
              onSkillUncheck(skill);
            } else {
              setRaterState?.(skill.id ?? null);
            }
          } else {
            onClick?.(skill);
          }
        }}
      >
        {skill.name}
      </span>
    </div>
  );
};

type Props = Omit<SkillNodeProps, 'skill'> & {
  skills: LegacySkill[];
};

const SkillPanel: React.FC<Props> = (props) => {
  const { skills } = props;

  return (
    <div className="skill-panel">
      {skills.map((skill) => (
        <SkillNode key={String(skill.id)} skill={skill} {...props} />
      ))}
    </div>
  );
};

export default SkillPanel;
